import type { ParameterDimensionDefinitionInOut } from '@festina/configuration-general-types/configuration'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'

import { DocumentationEntry } from '../../../components/documentationEntry/DocumentationEntry'
import { EditableMarkdownViewer } from '../../../components/markdownEditor/EditableMarkdownViewer'
import { useParameterContext } from '../../../context/parameterContext'
import { useUpdateCurrentParameterDefinitionDimension } from '../../../uiModels/parameters/useUpdateCurrentParameterDefinitionDimension'
import { ParameterDimensionExpression } from '../parameterViewer/ParameterDimensionExpression'
import { ParameterDimensionTypeEditor } from '../parameterViewer/ParameterDimensionTypeEditor'
import { ParameterDimensionTypeViewer } from '../parameterViewer/ParameterDimensionTypeViewer'

type Props = {
  data: ParameterDimensionDefinitionInOut
}

export const ParameterDimensionDefinitionDetails = ({ data }: Props) => {
  const translate = useTranslate()
  const updateDimensionOnCurrentParameter =
    useUpdateCurrentParameterDefinitionDimension()
  const { parameterDefinitionOperation } = useParameterContext()

  assertNotEmpty(parameterDefinitionOperation)

  const { isDraft } = parameterDefinitionOperation

  const handleUpdateDimensionDescription = (nextValue: Optional<string>) =>
    updateDimensionOnCurrentParameter?.({
      ...data,
      description: nextValue,
    })

  const description = data.description

  return (
    <>
      <DocumentationEntry
        title={translate(
          'parameterViewer.parameterDimensionDetails.description',
        )}
        value={
          <EditableMarkdownViewer
            key={description}
            value={description}
            onSave={isDraft ? handleUpdateDimensionDescription : undefined}
          />
        }
      />
      <DocumentationEntry
        title={translate('parameterViewer.parameterDimensionDetails.type')}
        value={
          isDraft ? (
            <ParameterDimensionTypeEditor data={data} isCentered={false} />
          ) : (
            <ParameterDimensionTypeViewer data={data} isCentered={false} />
          )
        }
      />
      <DocumentationEntry
        title={translate(
          'parameterViewer.parameterDimensionDetails.expression',
        )}
        value={
          <ParameterDimensionExpression data={data} isTransparent={false} />
        }
      />
    </>
  )
}
